/**
 * Community invite API for the public invite landing page and signed-in accept flow.
 * GET /community/connection-invites/{inviteId} — no auth required (links sent via SMS/email).
 * POST claim + PUT ACCEPTED — authenticated (iOS InviteAcceptanceView parity).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

export type InviteStatus = 'pending' | 'accepted' | 'expired';

export interface InviteDetails {
  inviteId: string;
  inviterName: string;
  inviterEmail?: string;
  status: InviteStatus;
}

export type InviteFetchResult =
  | { ok: true; invite: InviteDetails }
  | { ok: false; reason: 'not_found' | 'expired' | 'network' | 'unknown' };

export interface AcceptConnectionInviteResult {
  inviteId: string;
  status: string;
  tripId: string | null;
  alreadyAccepted: boolean;
}

export class InviteAcceptError extends Error {
  readonly statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.name = 'InviteAcceptError';
    this.statusCode = statusCode;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Fetch invite details for the landing page. Returns null-like result on failure
 * so the page can show generic copy without breaking.
 */
export async function fetchInviteDetails(inviteId: string): Promise<InviteFetchResult> {
  const url = `${API_BASE_URL}/community/connection-invites/${encodeURIComponent(inviteId)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 404) {
      return { ok: false, reason: 'not_found' };
    }

    if (!response.ok) {
      return { ok: false, reason: 'unknown' };
    }

    const data = (await response.json()) as Partial<InviteDetails> & {
      status?: string;
    };

    const rawStatus = String(data.status ?? 'pending').toLowerCase();
    if (rawStatus === 'expired') {
      return { ok: false, reason: 'expired' };
    }

    const inviterName = data.inviterName?.trim();
    if (!inviterName) {
      return { ok: false, reason: 'unknown' };
    }

    const status: InviteStatus =
      rawStatus === 'accepted' ? 'accepted' : 'pending';

    return {
      ok: true,
      invite: {
        inviteId: data.inviteId ?? inviteId,
        inviterName,
        inviterEmail: data.inviterEmail,
        status,
      },
    };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * Attach a pending email/SMS invite to the signed-in user (does not accept).
 * POST /community/connection-invites/claim
 */
export async function claimConnectionInvite(params: {
  inviteId: string;
  email: string;
}): Promise<void> {
  const inviteId = params.inviteId.trim();
  const email = params.email.trim();
  if (!inviteId || !email) {
    throw new InviteAcceptError(400, 'inviteId and email are required');
  }

  const headers = await buildAuthHeaders(true);
  const res = await fetch(`${API_BASE_URL}/community/connection-invites/claim`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ inviteId, email }),
  });

  if (!res.ok) {
    throw new InviteAcceptError(
      res.status,
      `Failed to claim invite (${res.status})`
    );
  }
}

/**
 * Accept a claimed connection invite.
 * PUT /community/connection-invites/{id} { status: "ACCEPTED" }
 */
export async function acceptConnectionInvite(
  inviteId: string
): Promise<AcceptConnectionInviteResult> {
  const id = inviteId.trim();
  if (!id) {
    throw new InviteAcceptError(400, 'inviteId is required');
  }

  const headers = await buildAuthHeaders(true);
  const res = await fetch(
    `${API_BASE_URL}/community/connection-invites/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: 'ACCEPTED' }),
    }
  );

  if (res.status === 409) {
    return {
      inviteId: id,
      status: 'ACCEPTED',
      tripId: null,
      alreadyAccepted: true,
    };
  }

  if (!res.ok) {
    // Some backends return 400/404 once already accepted — treat as already accepted when body hints.
    if (res.status === 400 || res.status === 404) {
      let bodyText = '';
      try {
        bodyText = (await res.text()).toLowerCase();
      } catch {
        bodyText = '';
      }
      if (
        bodyText.includes('accepted') ||
        bodyText.includes('already') ||
        bodyText.includes('not pending')
      ) {
        return {
          inviteId: id,
          status: 'ACCEPTED',
          tripId: null,
          alreadyAccepted: true,
        };
      }
    }
    throw new InviteAcceptError(
      res.status,
      `Failed to accept invite (${res.status})`
    );
  }

  let tripId: string | null = null;
  let status = 'ACCEPTED';
  try {
    const json = asRecord(await res.json());
    const rawTrip = json.tripId ?? json.trip_id;
    tripId = rawTrip != null && String(rawTrip).trim() ? String(rawTrip) : null;
    if (json.status != null) status = String(json.status);
  } catch {
    // empty / non-JSON body is fine
  }

  return {
    inviteId: id,
    status,
    tripId,
    alreadyAccepted: false,
  };
}
